import { cn } from "@/lib/utils";
import { INTERACTIVE_FOCUS } from "../lib/chrome";
import { useLandingCopy } from "../lib/copy";
import { LANDING_CONTENT_MEASURE_CLASS } from "../lib/layout";
import { resolveCompleteCtaHrefKey, resolveCompletePresentation } from "./questionnaire-complete-copy";
import { useQuestionnaireFlow } from "./questionnaire-context";
import { StepEnter } from "./step-enter";

const COMPLETE_CTA_CLASS =
	"inline-flex h-[46px] w-[156px] items-center justify-center bg-green-500 px-6 text-sm font-normal leading-[1.3] tracking-[-0.02em] whitespace-nowrap text-teal-500 no-underline hover:bg-green-400 focus-visible:bg-green-400";

/** Figma 3338:20896 — full-width last turn in the chat transcript. */
export function QuestionnaireComplete() {
	const { path, resolveCtaHref } = useQuestionnaireFlow();
	const { testimonial } = useLandingCopy();
	if (!path) return null;
	const href = resolveCtaHref(resolveCompleteCtaHrefKey(path));
	const presentation = resolveCompletePresentation(path);

	return (
		<section
			className={cn(
				LANDING_CONTENT_MEASURE_CLASS,
				"flex flex-col items-center gap-8 bg-white px-8 py-8 text-center md:px-12 md:py-10",
			)}
			data-questionnaire-complete=""
			aria-labelledby="questionnaire-complete-heading"
		>
			<div className="flex flex-col items-center gap-2">
				<StepEnter enabled delayMs={0} durationMs={900}>
					<h1 id="questionnaire-complete-heading" className="m-0 text-landing-section font-normal text-teal-500">
						{presentation.heading}
					</h1>
				</StepEnter>
				<StepEnter enabled delayMs={160} durationMs={900}>
					<p className="m-0 text-[18px] font-normal leading-[1.3] tracking-[-0.02em] text-teal-500">
						{presentation.subtitle}
					</p>
				</StepEnter>
			</div>
			<StepEnter enabled delayMs={320} durationMs={840}>
				<a className={cn(COMPLETE_CTA_CLASS, INTERACTIVE_FOCUS)} href={href} data-questionnaire-complete-cta="">
					{presentation.ctaLabel}
				</a>
			</StepEnter>
			<StepEnter enabled delayMs={480} durationMs={800} className="flex w-full flex-col items-center gap-6">
				<hr className="m-0 w-full border-t cc-border-grey-800" />
				<figure className="m-0 flex flex-col items-center justify-center gap-4">
					<blockquote className="m-0 max-w-[min(560px,100%)] text-[14px] font-normal italic leading-[1.3] tracking-[-0.02em] text-teal-500">
						{testimonial.quote}
					</blockquote>
					<img
						alt={testimonial.portraitAlt}
						className="size-12 shrink-0 object-cover object-[center_18%]"
						height={48}
						src={testimonial.portraitSrc}
						width={48}
					/>
					<figcaption className="flex shrink-0 flex-col items-center gap-1 text-[14px] font-normal leading-[1.3] tracking-[-0.02em] text-teal-500">
						{testimonial.brandLogoSrc ? (
							<img
								alt={testimonial.brandLogoAlt ?? ""}
								className="h-3.5 w-auto shrink-0 object-contain"
								src={testimonial.brandLogoSrc}
							/>
						) : null}
						<span>{testimonial.authorName}</span>
						<span className="font-normal">{testimonial.authorRole}</span>
					</figcaption>
				</figure>
			</StepEnter>
		</section>
	);
}
